import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ArrowLeft, ExternalLink, CheckCircle2 } from 'lucide-react';
import { ContentProtection } from '@/components/content-protection';
import { useYouTubeProgress } from './youtube-progress';
import { detectContentType, toEmbedUrl, type ContentType } from './content-viewer.detect';
import { LessonImage, ViewerFallback } from './content-viewer.parts';

export function ContentViewer({
  url,
  htmlContent,
  title,
  onClose,
  onScrolledToEnd,
  onVideoProgress,
}: {
  url: string;
  /** HTML content for "content" type lessons (rendered inline). */
  htmlContent?: string;
  title: string;
  onClose: () => void;
  /** Fired once when the user scrolls to the bottom of the content area. */
  onScrolledToEnd?: () => void;
  /** Fired with the high-watermark % (0-100) as the user watches a native video. */
  onVideoProgress?: (percent: number) => void;
}) {
  const contentType = useMemo(
    () => (htmlContent ? ('html' as const) : detectContentType(url)),
    [url, htmlContent],
  );
  const embedUrl = useMemo(
    () => (htmlContent ? '' : toEmbedUrl(url, contentType as ContentType)),
    [url, contentType, htmlContent],
  );
  // Lesson HTML is authored in the admin panel; sanitise before rendering so a
  // malicious/compromised author cannot store an XSS payload that runs in every
  // colaborador's browser. DOMPurify strips scripts, event handlers, iframes and
  // javascript: URLs while preserving legitimate rich-text formatting.
  const sanitizedHtml = useMemo(
    () => (htmlContent ? DOMPurify.sanitize(htmlContent) : ''),
    [htmlContent],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const videoHighWaterRef = useRef(0);
  const [videoPercent, setVideoPercent] = useState(0);

  // YouTube Iframe API progress tracking
  const ytIframeId = 'yt-player-content-viewer';
  const ytEnabled = contentType === 'youtube' && !!onVideoProgress;
  const ytPercent = useYouTubeProgress(ytIframeId, ytEnabled, onVideoProgress);

  // Merge native video + YouTube percent for the badge display
  const displayPercent = contentType === 'youtube' ? ytPercent : videoPercent;

  const handleTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const vid = e.currentTarget;
      if (!vid.duration || !Number.isFinite(vid.duration) || !onVideoProgress) return;
      const pct = Math.round((vid.currentTime / vid.duration) * 100);
      if (pct > videoHighWaterRef.current) {
        videoHighWaterRef.current = pct;
        setVideoPercent(pct);
        onVideoProgress(pct);
      }
    },
    [onVideoProgress],
  );

  // Fire ended event at 100% in case timeupdate misses the final frame
  const handleVideoEnded = useCallback(() => {
    if (!onVideoProgress) return;
    if (videoHighWaterRef.current < 100) {
      videoHighWaterRef.current = 100;
      setVideoPercent(100);
      onVideoProgress(100);
    }
  }, [onVideoProgress]);

  // An image does not count as read until it is painted: until its onLoad,
  // neither the mount check nor scrolling over the placeholder may fire. It
  // tracks WHICH url loaded, so a re-signed url starts over.
  const loadedImageUrlRef = useRef<string | null>(null);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || firedRef.current || !onScrolledToEnd) return;
    if (contentType === 'image' && loadedImageUrlRef.current !== url) return;

    // Threshold of 40px to avoid requiring pixel-perfect scrolling
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) {
      firedRef.current = true;
      setReachedEnd(true);
      onScrolledToEnd();
    }
  }, [onScrolledToEnd, contentType, url]);

  const handleImageLoaded = useCallback(() => {
    loadedImageUrlRef.current = url;
    checkScroll();
  }, [checkScroll, url]);

  // Check on mount — content might be short enough to not need scroll.
  // Uses ResizeObserver to fire after layout is settled, avoiding
  // synchronous setState inside the effect body (react-hooks/set-state-in-effect).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !onScrolledToEnd) return;

    const ro = new ResizeObserver(() => {
      checkScroll();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll, onScrolledToEnd]);

  return (
    <ContentProtection>
      <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
            {reachedEnd && onScrolledToEnd && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />
                Lectura completa
              </span>
            )}
            {onVideoProgress && displayPercent > 0 && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {displayPercent}% visto
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {contentType !== 'html' && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Abrir en pestaña
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al curso
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={scrollRef} onScroll={checkScroll} className="flex-1 overflow-auto p-4">
          {(contentType === 'youtube' || contentType === 'vimeo') && (
            <div className="mx-auto aspect-video max-w-4xl overflow-hidden rounded-lg">
              <iframe
                id={contentType === 'youtube' ? ytIframeId : undefined}
                src={embedUrl}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="h-full w-full"
              />
            </div>
          )}

          {contentType === 'video' && (
            <div className="mx-auto max-w-4xl overflow-hidden rounded-lg">
              <video
                controls
                crossOrigin="anonymous"
                disablePictureInPicture
                controlsList="nodownload"
                className="h-full w-full"
                src={url}
                onTimeUpdate={onVideoProgress ? handleTimeUpdate : undefined}
                onEnded={onVideoProgress ? handleVideoEnded : undefined}
              >
                <track kind="captions" />
              </video>
            </div>
          )}

          {contentType === 'pdf' && (
            <iframe
              src={url}
              title={title}
              className="mx-auto h-full w-full max-w-4xl rounded-lg"
            />
          )}

          {contentType === 'image' && (
            // key: a re-signed URL starts over (retries after an error).
            <LessonImage key={url} url={url} title={title} onLoaded={handleImageLoaded} />
          )}

          {contentType === 'office' && (
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
              title={title}
              className="mx-auto h-full w-full max-w-4xl rounded-lg"
            />
          )}

          {contentType === 'html' && htmlContent && (
            <div className="mx-auto max-w-4xl rounded-lg border border-border bg-card p-6">
              <div
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              />
            </div>
          )}

          {contentType === 'unknown' && (
            <ViewerFallback url={url} message="No se puede previsualizar este contenido." />
          )}
        </div>
      </div>
    </ContentProtection>
  );
}
